import { CLIENT_CONTRACT } from "./client_contract"
import { haversineMeters } from "./geo_math"

// Gesture-learned follow camera — the web port of the app's tested module
// (react-native/src/map/cameraGesture.js): decide whether a finished user
// gesture was LOOK-AROUND (a pan — the camera preference must not change) or a
// CAMERA ADJUSTMENT (zoom/tilt with the center staying put — the new zoom and
// tilt become the saved follow preference). Pure, so it's unit-testable.

// A gesture whose center drifted less than this many screen pixels is "not a pan".
// Pinches wobble the center a little; real pans (and cursor-anchored wheel zooms
// near the screen edge) move it far more.
export const GESTURE_PAN_MAX_PX = 40

// A pan bigger than this while in Live means "let me explore" — the map drops
// out of Live instead of snapping back after the auto-recenter delay.
export const EXIT_LIVE_PAN_PX = 80

const CAMERA_POLICY = CLIENT_CONTRACT.mobile.camera

export const FOLLOW_ZOOM_MIN = CAMERA_POLICY.followZoom.min
export const FOLLOW_ZOOM_MAX = CAMERA_POLICY.followZoom.max
export const FOLLOW_PITCH_MAX = CAMERA_POLICY.followPitch.max

// Settings keep one decimal of precision (19.3), matching what gestures produce;
// the Settings selects display the nearest whole option.
export const round1 = x => {
  const n = Number(x)
  return Number.isFinite(n) ? Math.round(n * 10) / 10 : null
}

const clamp = (x, min, max) => Math.max(min, Math.min(max, x))

// Accept MapLibre's {lat, lng} and the app's {lat, lon} interchangeably.
const lonOf = c => (c?.lon != null ? c.lon : c?.lng)

// Ground meters per screen pixel at a zoom/latitude (512px world tiles).
function metersPerPixel(lat, zoom) {
  return (156543.03392 * Math.cos((lat * Math.PI) / 180)) / 2 ** (Number(zoom) || 0)
}

function metersBetween(a, b) {
  return haversineMeters(a.lat, lonOf(a), b.lat, lonOf(b))
}

/** Screen-pixel distance the center moved between gesture start and end. */
export function panDistancePx(start, end) {
  if (!start?.center || !end?.center) return 0
  const meters = metersBetween(start.center, end.center)
  const mpp = metersPerPixel(end.center.lat, end.zoom ?? start.zoom)
  return mpp > 0 ? meters / mpp : 0
}

/**
 * Classify a finished gesture. `start`/`end`: { center, zoom, pitch }.
 * Returns null for a pan, a no-op, or unusable input; otherwise the camera
 * preference to save: { zoom?, pitch? } (rounded to one decimal, clamped).
 */
export function gestureAdjustment(start, end) {
  if (!start?.center || !end?.center) return null
  if (panDistancePx(start, end) > GESTURE_PAN_MAX_PX) return null // panned → look-around

  const zoom = round1(end.zoom)
  const pitch = round1(end.pitch)
  const zoomChanged = zoom != null && start.zoom != null && Math.abs(zoom - start.zoom) >= 0.05
  const pitchChanged = pitch != null && start.pitch != null && Math.abs(pitch - start.pitch) >= 0.5
  if (!zoomChanged && !pitchChanged) return null

  const out = {}
  if (zoomChanged) out.zoom = clamp(zoom, FOLLOW_ZOOM_MIN, FOLLOW_ZOOM_MAX)
  if (pitchChanged) out.pitch = clamp(pitch, 0, FOLLOW_PITCH_MAX)
  return out
}
